import {
  BreakPoints,
  BP,
  V,
  type BreakPointsInstance,
  type Line,
} from '@shared/helpers/breakpoints/BreakPoints';

import {
  ElementCategoryID,
  ProductCategoryId,
} from '@shared/models/element-categories.model';
import { PartialRecord } from '@shared/models/type-helpers.model';

type LevelString = `level ${number}`;
type LevelBreakpointInstance = BreakPointsInstance<LevelString, number>;

const generateLevelBreakpoints = (levels: number): LevelBreakpointInstance => {
  if (levels <= 1)
    throw new Error("Can't generate breakpoints with no or a single level");
  const line: Line<LevelString> = [V('level 0')];
  for (let i = 1; i <= levels; i++)
    line.unshift(V(`level ${i}`), BP(-i * 10, ']'));

  return BreakPoints(line);
};

const defaultBreakpoints = generateLevelBreakpoints(9);

const levelMap: PartialRecord<ProductCategoryId, LevelBreakpointInstance> = {
  [ElementCategoryID.Concrete]: generateLevelBreakpoints(9),
};

/**
 * Climate impact level label for bulk-edit style UI (e.g. `"level 3"`).
 *
 * @param category - Product category; when unknown a default scorer is used.
 * @param percentAdjustment - Adjustment from baseline emissions as percent (e.g. -30 for -30%).
 */
export const getProductClimateImpactLevel = (
  category: ProductCategoryId | undefined,
  percentAdjustment: number,
): LevelString => {
  const breakpoints = (category && levelMap[category]) ?? defaultBreakpoints;
  return breakpoints.getValue(percentAdjustment);
};

/**
 * Numeric level index from {@link getProductClimateImpactLevel} (e.g. `"level 2"` → `2`).
 */
export const getProductClimateImpactLevelNumber = (
  category: ProductCategoryId | undefined,
  percentAdjustment: number,
): number => {
  const label = getProductClimateImpactLevel(category, percentAdjustment);
  const match = /^level (\d+)$/.exec(label);
  return match !== null ? Number(match[1]) : 0;
};
